/**
 * Soul Content — the source of truth for the six Inner Exploration
 * practices. Each practice follows the same seven-step progression:
 *
 *   arrive → understand → recognize → deeper → listening → practice → integrate
 *
 * Keeping copy here (rather than sprinkled through components) means the
 * page shell stays small and predictable, and the writing can be tuned
 * without touching UI code.
 *
 * IMAGES — every practice + path + regulation sub-card owns a unique
 * editorial photograph. All URLs live in `lib/editorialImages.js`; do
 * not hard-code them here.
 */

import { EDITORIAL } from "@/lib/editorialImages";

export const INNER_PRACTICES = [
  {
    slug: "inner-child",
    title: "Inner Child",
    eyebrow: "Inner Exploration",
    tagline: "Meet the parts of you that still need compassion.",
    icon: "sprout",
    image: EDITORIAL.soulInnerChild,
    arrive:
      "Before looking for an answer, notice what is already here. Nothing needs to be solved in this moment.",
    understand: [
      "The parts of you shaped when you were very young are still with you — quietly influencing how you feel about love, safety, belonging, and worth.",
      "This isn't about revisiting the past to change it. It's about noticing which younger parts of you still need something from who you are now.",
    ],
    recognize: [
      "You may notice a younger part of yourself when a small moment produces a very big feeling.",
      "You might recognize it in the way you interpret silence, distance, or someone else's mood as being about you.",
      "Sometimes it can look like needing to earn love, please people, stay small, or perform to be safe.",
    ],
    deeper: [
      "What did you need when you were young that you didn't quite receive?",
      "What did you learn — quietly — about love, safety, or belonging?",
      "Where might those early lessons still shape the way you move through your days?",
      "What situations most often make you feel younger than you are?",
      "What does that younger part of you need from who you are now?",
    ],
    listening:
      "What is the part of me that feels small asking to be heard right now?",
    practice: {
      kind: "visualization",
      title: "A quiet meeting",
      steps: [
        "Close your eyes, if that feels comfortable. Take one slow breath.",
        "Picture yourself as a young child — an age that comes easily to you.",
        "Notice them without changing anything. What are they wearing? What are they doing?",
        "Sit near them. Let them know they don't have to explain anything to you.",
        "Silently offer: \"I see you. I'm here. You didn't do anything wrong.\"",
        "Stay for a breath or two, then open your eyes gently.",
      ],
      closing:
        "You cannot change what your younger self experienced. But you can change how you meet that part of yourself today.",
    },
    integrate:
      "What do I want to carry forward from this — one small way I can meet that younger part of me this week?",
  },
  {
    slug: "the-shadow",
    title: "The Shadow",
    eyebrow: "Inner Exploration",
    tagline: "Explore what you've learned to hide.",
    icon: "moon",
    image: EDITORIAL.soulShadow,
    arrive:
      "Take a moment before you begin. There is nothing here to perform. There is something here to notice.",
    understand: [
      "\"The Shadow\" is a reflective framework — not a diagnosis. It refers to the qualities, desires, emotions, or parts of ourselves we've learned to hide, judge, or deny.",
      "The parts we reject don't disappear. They quietly influence what triggers us, what we envy, what we criticize, and where we feel most defensive.",
      "Meeting the shadow isn't about becoming darker. It's about becoming more whole.",
    ],
    recognize: [
      "You may notice a shadow at play when someone else's quality triggers an unusually strong reaction in you.",
      "Sometimes it can look like harsh judgment — of others, or of yourself.",
      "You might recognize it in the parts of yourself you work hardest not to be.",
    ],
    deeper: [
      "What qualities in other people strongly trigger you?",
      "What do you judge most harshly — in others or in yourself?",
      "Which parts of yourself do you try hardest not to be?",
      "What do you fear others would see if you stopped managing their perception of you?",
      "Could something you reject be pointing to a need, a fear, a desire, or a boundary you haven't yet named?",
    ],
    listening:
      "What is this touching inside of me — what is it asking me to acknowledge?",
    practice: {
      kind: "writing",
      title: "A page you will not send",
      steps: [
        "Take out paper, or open a private note.",
        "Bring to mind a person or quality that has been triggering you lately.",
        "Write, without editing: \"What I resist in them is…\"",
        "Then write: \"What that might be showing me about myself is…\"",
        "You are not agreeing that they are right. You are noticing what is being touched in you.",
        "When you finish, take one slow breath. Nothing is required of you.",
      ],
      closing:
        "Curiosity, not shame. The shadow softens when it is met — not when it is punished.",
    },
    integrate:
      "What do I want to carry forward — one part of myself I'm willing to meet with more curiosity this week?",
  },
  {
    slug: "self-inquiry",
    title: "Self-Inquiry",
    eyebrow: "Inner Exploration",
    tagline: "Ask the questions beneath the answers.",
    icon: "reflect",
    image: EDITORIAL.soulSelfInquiry,
    arrive:
      "You don't need to arrive at an answer today. You only need to be honest with the question.",
    understand: [
      "Much of our life is shaped by yeses we never chose — expectations, habits, fears, and stories inherited from people who loved us.",
      "Self-inquiry is the quiet practice of noticing the difference between what you genuinely want, what you think you should want, and what fear is doing the wanting for you.",
    ],
    recognize: [
      "You may notice this in the yeses that leave you tired.",
      "Sometimes it can look like a life that looks right from the outside but feels slightly off from the inside.",
      "You might recognize it in the moments when your body knows the answer before your mind allows it.",
    ],
    deeper: [
      "What do you actually want — not what you should want?",
      "Where are you saying yes while feeling no?",
      "Whose expectations are you carrying that were never quite yours?",
      "Where might you be abandoning yourself to maintain approval or comfort?",
      "What feels true even when it is uncomfortable?",
    ],
    listening:
      "What feels aligned with the person I am becoming — even if it's inconvenient to admit?",
    practice: {
      kind: "reflection",
      title: "One honest sentence",
      steps: [
        "Bring one recent yes to mind — something you agreed to that feels heavier than it should.",
        "Sit with it for a breath. Notice what your body says before your mind explains.",
        "Complete this sentence, in your own words: \"If I were being honest with myself, I'd admit that…\"",
        "Do not rush. Do not solve. Just let the sentence land.",
      ],
      closing:
        "Clarity often arrives quietly — not as certainty, but as recognition.",
    },
    integrate:
      "What do I want to carry forward — one small alignment I'm willing to honor this week?",
  },
  {
    slug: "forgiveness",
    title: "Forgiveness",
    eyebrow: "Inner Exploration",
    tagline: "Release what no longer needs to define you.",
    icon: "release",
    image: EDITORIAL.soulForgiveness,
    arrive:
      "Nothing needs to be resolved in this moment. Simply notice what has been asking for your attention.",
    understand: [
      "Forgiveness is often misunderstood. It does not require pretending, excusing, forgetting, removing your boundaries, or reconciling with anyone.",
      "Forgiveness is a quiet inner act — releasing the amount of space an experience continues to occupy inside of you.",
      "A boundary can stay firmly in place, even as the weight begins to leave.",
    ],
    recognize: [
      "You may notice unforgiveness in a story you rehearse when your mind is quiet.",
      "Sometimes it can look like a small tightening in your chest at the mention of a name.",
      "You might recognize it in the way an old moment still colors the way you meet new ones.",
    ],
    deeper: [
      "What are you still carrying — quietly, without meaning to?",
      "What did this experience take from you?",
      "What did it teach you, whether you wanted the lesson or not?",
      "What now belongs to you — that you can honor without continuing to hold?",
      "What are you ready to stop carrying, even without their apology?",
    ],
    listening:
      "What part of me is ready to be lighter — and what is it asking me to release?",
    practice: {
      kind: "writing",
      title: "A letter for you, not for them",
      steps: [
        "Write, without sending: \"What you took from me was…\"",
        "Then: \"What I learned, even if I didn't want to, was…\"",
        "Then: \"What I am ready to stop carrying is…\"",
        "Close with one sentence: \"I do not have to hold this the way I once did.\"",
        "You can keep the letter, tear it, burn it, or save it privately. It was never for them.",
      ],
      closing:
        "A boundary can remain even when the weight begins to leave.",
    },
    integrate:
      "What do I want to carry forward — and what am I willing to set down, even just a little, this week?",
  },
  {
    slug: "grief-and-letting-go",
    title: "Grief & Letting Go",
    eyebrow: "Inner Exploration",
    tagline: "Make space for what you've lost and what is changing.",
    icon: "leaf",
    image: EDITORIAL.soulGrief,
    arrive:
      "Grief does not need to be explained to be valid. Take one slow breath. You are allowed to feel this.",
    understand: [
      "Grief lives in far more places than we're taught. It shows up around endings of every kind — relationships, friendships, homes, identities, dreams, chapters, and versions of ourselves we can't return to.",
      "There is also grief for the things that never became what we hoped they would.",
      "Letting go is not forgetting. It is loosening our grip enough to let something continue to matter without continuing to hold us.",
    ],
    recognize: [
      "You may notice grief as unexpected tears at ordinary moments.",
      "Sometimes it can look like an ache for a version of your life that isn't happening.",
      "You might recognize it in a quiet reluctance to close a chapter that has already ended.",
    ],
    deeper: [
      "What are you grieving — even if it doesn't feel large enough to name?",
      "What did this mean to you?",
      "What did you imagine it would become?",
      "What are you afraid letting go might mean about you or about the loss?",
      "What can you honor without continuing to hold?",
    ],
    listening:
      "What does the part of me that is grieving most need me to acknowledge?",
    practice: {
      kind: "spoken",
      title: "A quiet honoring",
      steps: [
        "Bring the loss into your awareness. It does not have to be dramatic to count.",
        "Take one slow breath.",
        "Say, quietly or in your mind: \"This mattered to me. This changed me. And I am still becoming.\"",
        "Sit for one more breath. Let the words land wherever they land.",
      ],
      closing:
        "You are allowed to grieve what was, and also to keep becoming.",
    },
    integrate:
      "What do I want to carry forward — one small way I can honor this loss without letting it carry me?",
  },
  {
    slug: "boundaries",
    title: "Boundaries",
    eyebrow: "Inner Exploration",
    tagline: "Honor what is yours.",
    icon: "threshold",
    image: EDITORIAL.soulBoundaries,
    arrive:
      "You do not need to justify your no. Take one slow breath before you begin.",
    understand: [
      "A boundary is not a wall, a punishment, or an attempt to control another person's behavior.",
      "A boundary is a quiet clarity — about what you will choose, allow, participate in, or step away from.",
      "Boundaries live in more places than we realize: time, energy, attention, body, relationships, communication, values, and emotional space.",
    ],
    recognize: [
      "You may notice a missing boundary in the resentment that shows up around certain people or requests.",
      "Sometimes it can look like a chronic sense of being drained by things you agreed to.",
      "You might recognize it in the yeses you say because you fear the reaction to a no.",
    ],
    deeper: [
      "Where in your life are you feeling resentment right now?",
      "Where do you repeatedly override yourself?",
      "Where are you saying yes because you fear how someone will respond to no?",
      "What are you no longer available for?",
      "What becomes possible when you honor this boundary?",
    ],
    listening:
      "What is the person I am becoming quietly asking me to protect?",
    practice: {
      kind: "spoken",
      title: "A sentence you can return to",
      steps: [
        "Bring to mind one situation where a boundary feels missing.",
        "Write, or say quietly: \"I am no longer available for ______.\"",
        "Then: \"What I choose instead is ______.\"",
        "Notice how your body responds to the words. There may be some resistance. That is part of it.",
        "You do not have to send this to anyone. You only have to know it is true.",
      ],
      closing:
        "You can care about someone and still honor yourself.",
    },
    integrate:
      "What do I want to carry forward — one small boundary I'm willing to hold this week?",
  },
];

export const REGULATION_PRACTICES = [
  {
    slug: "breath",
    title: "Breath",
    tagline: "Return to the rhythm that is always with you.",
    image: EDITORIAL.regulationBreath,
    icon: "breath",
    comingSoon: false,
    body:
      "A set of breath practices — from conscious breathing to the physiological sigh — quiet doorways back to the present.",
  },
  {
    slug: "nervous-system",
    title: "Nervous System",
    tagline: "Learn to notice what your body is telling you.",
    image: EDITORIAL.regulationNervous,
    icon: "body",
    comingSoon: false,
    body:
      "A gentle experience for noticing your body's signals, recognizing your patterns, and learning what actually supports you.",
  },
];

export const SOUL_PATHS = [
  {
    slug: "regulation-presence",
    eyebrow: "Path One",
    title: "Regulation & Presence",
    tagline: "Come back to yourself.",
    body:
      "Practices to help you slow down, breathe, ground, and reconnect with the present.",
    contains: ["Breath", "Nervous System"],
    image: EDITORIAL.soulPathRegulation,
  },
  {
    slug: "inner-exploration",
    eyebrow: "Path Two",
    title: "Inner Exploration",
    tagline: "Meet what lives beneath the surface.",
    body:
      "Explore the patterns, emotions, experiences, beliefs, and parts of yourself asking to be understood.",
    contains: [
      "Inner Child",
      "The Shadow",
      "Self-Inquiry",
      "Forgiveness",
      "Grief & Letting Go",
      "Boundaries",
    ],
    image: EDITORIAL.soulPathExploration,
  },
];

export function findPractice(slug) {
  return INNER_PRACTICES.find((p) => p.slug === slug) || null;
}
